/**
 * 데이터베이스 모델 인터페이스 정의
 * ERD에 기반한 타입 정의입니다.
 */

// 환자 정보 인터페이스
export interface Patient {
  id?: number; // 환자 고유 ID
  birthDate?: Date | string; // 생년월일
  age?: number; // 나이 // ai-reviews 전용 // 측정 시점에 계산된 나이
  gender?: string; // 성별
  pregnancyStatus?: boolean; // 임신여부
  diseases?: string; // 질병명
  allergies?: string; // 알러지 정보
  consultationNotes?: string; // 상담내용
  memo?: string; // 메모
  createdAt?: Date; // 생성 시간
  updatedAt?: Date; // 수정 시간
  deletedAt?: Date | null; // 삭제 시간
  userId?: string | null; // 사용자 ID (소유자)
  prescriptions?: Prescription[]; // 처방 목록
  otc?: Prescription[]; // 일반약 목록
  hff?: Prescription[]; // 건강기능 식품 목록
  aiReviews?: AIReview[]; // AI 검토 결과 목록
  prescriptionGroups?: PrescriptionGroup[];

  lastAiReviewAt?: Date; // 마지막 AI 검토 시간
  aiReviewCnt?: number; // AI 검토 횟수

  nhisLastStatus?:
    | "PENDING"
    | "AUTH_REQUESTED"
    | "FETCHED"
    | "DELIVERED"
    | "FAILED"
    | "EXPIRED"
    | "CANCELLED"
    | null;
  nhisLastStatusAt?: string; // ISO 8601, delivered_at ?? created_at

  schedules?: Schedule[];
  consultationRecords?: ConsultationRecordListItem[]; // 상담 녹음 기록 목록
  drugIdentifications?: DrugIdentification[]; // 의약품 식별 목록
}

// 일정 인터페이스
export interface Schedule {
  id?: number;
  userId?: string;
  user?: User;
  patientId?: number;
  patient?: Patient;
  scheduledAt?: Date;
  notes?: string;
  doneAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  deletedAt?: Date;
}

export const EMPTY_PATIENT: Patient = {
  id: 0,
  gender: "",
  diseases: "",
  allergies: "",
};

// 약품 정보 인터페이스
export interface Drug {
  id?: number; // 약품 고유 ID

  // KPIS 데이터
  mainCode?: string; // 대표코드
  standardCode?: string; // 표준코드
  itemCode?: string; // 품목기준코드
  productName?: string; // 한글상품명
  companyName?: string; // 업체명
  specialtyOtc?: string; // 전문/일반
  drugSpecification?: string; // 약품규격
  totalQuantity?: number; // 제품총수량
  dosageForm?: string; // 제형구분
  packageForm?: string; // 포장형태
  approvalDate?: Date; // 품목허가일자
  productCode?: string; // 제품코드(개정후)
  ingredientCode?: string; // 일반명코드(성분명코드)
  notes?: string; // 비고
  cancellationDate?: Date | null; // 취소일자
  transferAnnouncementDate?: Date | null; // 양도양수적용(공고)일자
  transferEndDate?: Date | null; // 양도양수종료일자
  serialNumberOmission?: boolean; // 일련번호생략여부
  serialNumberOmissionReason?: string | null; // 일련번호생략사유
  atcCode?: string; // ATC코드
  meftDivNo?: string; // 약효분류번호 (약제비 목록 분류)
  specialManagementType?: string; // 특수관리약품구분
  medicationReadingDeviceType?: string; // 의약품판독장비구분
  createdAt?: Date; // 생성 시간
  updatedAt?: Date; // 수정 시간
  deletedAt?: Date | null; // 삭제 시간
  crawledAt?: Date | null; // 크롤링된 시간
  productLabelCount?: number; // 허가사항 레코드 개수 (-1: 미크롤링, 0: 데이터없음, N: 보유)

  // 약제비 목록 데이터
  insuranceCode?: string; // 제품코드
  dosageUnit?: string; // 투약량 단위
  mainIngredient?: string; // 주성분

  productLabels?: DrugProductLabel[]; // 허가사항
}

export type PrescriptionSource = "manual" | "excel" | "ocr" | "nhis" | "identification";

// 처방 정보 인터페이스
export interface Prescription {
  id?: number; // 처방 항목 ID
  prescriptionGroupId?: string; // 처방전 그룹 ID
  prescriptionSequence?: number; // 처방 내 순서
  type?: string; // 처방 타입 (etc, otc, hff)
  patientId?: number; // 환자 ID
  itemCode?: string; // 품목기준코드
  prescriptionDate?: Date | string; // 처방일
  hospital?: string; // 병원명
  medicationName?: string; // 처방의약품의명칭
  dosagePerAdministration?: number; // 1회투약량
  dosageUnit?: string; // 투약량 단위
  timesPerDay?: number; // 1일투약횟수
  daysOfAdministration?: number; // 투약일수
  usageInstruction?: string; // 용법
  standardCode?: string; // 표준코드
  insuranceCode?: string; // 보험코드
  meftDivNo?: string; // 약효분류번호 (약제비 목록 분류, 처방 시점 스냅샷)
  ingredientCode?: string; // 일반명코드(성분명코드, 처방 시점 스냅샷)
  atcCode?: string; // ATC코드 (처방 시점 스냅샷)
  pharmacy?: string; // 조제기관(약국) — 심평원 원문 (CN-08)
  ingredientName?: string; // 성분명 원문 — 심평원 원문 (CN-08)
  drugEfficacy?: string; // 약효분류 — 심평원 원문 (CN-08)
  drugMainIngredient?: string | null; // 약품 DB 주성분 원문 (목록 조회 응답 전용, DB 칸 아님)
  notes?: string; // 특이사항
  source?: PrescriptionSource | ""; // 처방전 입력 출처 (빈문자열=미상)
  aiIncluded?: boolean; // AI 분석 포함 여부
  createdAt?: Date; // 생성 시간
  deletedAt?: Date | null; // 삭제 시간
  userId?: string | null; // 사용자 ID (소유자)
  patient?: Patient; // 환자 정보
  drug?: Drug; // 약품 정보
}

export const EMPTY_PRESCRIPTION: Prescription = {
  id: 0,
  prescriptionGroupId: "",
  prescriptionSequence: 0,
  type: "etc",
  patientId: 0,
  itemCode: "",
  prescriptionDate: null,
  hospital: "",
  medicationName: "",
  dosagePerAdministration: undefined,
  dosageUnit: "",
  timesPerDay: undefined,
  daysOfAdministration: undefined,
  usageInstruction: "",
  insuranceCode: "",
  notes: "",
  source: undefined,
};

export const EMPTY_PRESCRIPTION_GROUP: PrescriptionGroup = {
  groupId: "",
  prescriptionDate: null,
  hospital: "",
  prescriptions: [{ ...EMPTY_PRESCRIPTION }],
  patientId: 0,
};

// 약품 라벨 인터페이스
export interface DrugProductLabel {
  id: number;
  itemCode: string;
  module: string;
  submodule: string;
  subnumber: string;
  detail: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
  userId?: string;
}

// 프롬프트 타입 정의
export enum PromptType {
  MEDICATION_COUNSELING = "medication_counseling",
  CMM_2 = "cmm_2",
  LCMM = "lcmm",
  FREE_QUESTION = "free_question",
  // MEDICATION_CASCADE = "medication_cascasde",
  // DISEASE_CONTRAINDICATION_ANALYSIS = "disease_contraindication_analysis",
  // INTERACTION_ANALYSIS_DETAILS = "interaction_analysis(details)",
  // INTERACTION_ANALYSIS_SHORT = "interaction_analysis(short)",
  // SIDE_EFFECT_CENTER_ANALYSIS = "side_effect_center_analysis",
  // SIDE_EFFECT_PHARMACIST_ANALYSIS = "side_effect_pharmacist_analysis",
  // MEDICATION_COUNSELING_RESULTS = "medication_counseling_results",
  // DOCTOR_SUBMISSION = "doctor_submission",
  // OPINION_EVIDENCE_ENHANCEMENT = "opinion_evidence_enhancement",
  // SHORT_VERSION = "short_version",
  // PATIENT_EDUCATION = "patient_education",
  GUIDELINE_BEERS2023 = "guideline_beers2023",
  GUIDELINE_SPSTv3 = "guideline_spstv3",
  GUIDELINE_LCMM = "guideline_lcmm",
  GUIDELINE_CCMM = "guideline_ccmm",
  GUIDELINE_CMM_HTNDBS = "guideline_cmm_htndbs",
  GUIDELINE_KABS = "guideline_kabs",
  GUIDELINE_KDGERI = "guideline_kdgeri",
  GUIDELINE_PP_ADHERENCE_CHECK = "guideline_pp_adherence_check",
  GUIDELINE_PP_EDUCATION_MATERIALS = "guideline_pp_education_materials",
  GUIDELINE_PP_RISK_AGENTS = "guideline_pp_risk_agents",
  GUIDELINE_POLY = "guideline_poly",

  PHARMACY_PATIENT_EDU = "pharmacy_patient_edu",
  POLY_PHARMACY_PATIENT_EDU = "poly_pharmacy_patient_edu",

  INTERNAL_PRESCRIPTION_IMAGE = "internal_prescription_image",
  INTERNAL_PRESCRIPTION_OCR_TEXT = "internal_prescription_ocr_text",
  INTERNAL_REVIEW_EVALUATION = "internal_review_evaluation",

  // 상담 기록
  CONSULTATION = "consultation",

  // 룰 추출 프롬프트(dose-review Phase 2) — 관리 화면 노출 전용.
  // 환자 분석/AI 리뷰 경로엔 노출하지 않는다(D-14, AnalysisOptionsTemplate에서 제외·백엔드 거절).
  DOSE_EXTRACT_STEP2 = "dose_extract_step2",
  DOSE_EXTRACT_STEP3 = "dose_extract_step3",
}

/**
 * 프롬프트 타입 한글 이름 매핑
 * - 모든 컴포넌트에서 공통으로 사용하는 매핑입니다.
 * - 매핑 변경 시 이 파일만 수정하면 모든 곳에 적용됩니다.
 */
export const PROMPT_TYPE_KOREAN_NAMES = {
  [PromptType.MEDICATION_COUNSELING]: "다제약물관리",
  [PromptType.CMM_2]: "통합약물관리",
  [PromptType.LCMM]: "요양시설",
  [PromptType.FREE_QUESTION]: "자유 질문",
  // [PromptType.MEDICATION_CASCADE]: "처방연쇄 분석",
  // [PromptType.DISEASE_CONTRAINDICATION_ANALYSIS]: "질병 금기사항 분석",
  // [PromptType.INTERACTION_ANALYSIS_DETAILS]: "상호작용 분석 (상세)",
  // [PromptType.INTERACTION_ANALYSIS_SHORT]: "상호작용 분석 (요약)",
  // [PromptType.SIDE_EFFECT_CENTER_ANALYSIS]: "부작용 분석 (센터)",
  // [PromptType.SIDE_EFFECT_PHARMACIST_ANALYSIS]: "부작용 분석 (약사)",
  // [PromptType.MEDICATION_COUNSELING_RESULTS]: "약물 상담 결과",
  // [PromptType.DOCTOR_SUBMISSION]: "의사 제출용",
  // [PromptType.OPINION_EVIDENCE_ENHANCEMENT]: "의견 근거 강화",
  // [PromptType.SHORT_VERSION]: "요약 버전",
  // [PromptType.PATIENT_EDUCATION]: "환자 교육용",
  [PromptType.GUIDELINE_BEERS2023]: "📘BEER's 기준 2023",
  [PromptType.GUIDELINE_SPSTv3]: "📘STOP & START v3",
  [PromptType.GUIDELINE_LCMM]: "📘요양시설용 검토도구",
  [PromptType.GUIDELINE_CCMM]: "📘통합약물관리 검토도구",
  [PromptType.GUIDELINE_CMM_HTNDBS]: "📘고혈압당뇨 약제선택기준",
  [PromptType.GUIDELINE_KABS]: "📘항콜린점수",
  [PromptType.GUIDELINE_KDGERI]: "📘안전관리원_노인",
  [PromptType.GUIDELINE_PP_ADHERENCE_CHECK]: "📘복약이행도 체크❌",
  [PromptType.GUIDELINE_PP_EDUCATION_MATERIALS]: "📘환자교육 자료❌",
  [PromptType.GUIDELINE_PP_RISK_AGENTS]: "📘집중관리약제목록❌",
  [PromptType.GUIDELINE_POLY]: "📘다제 종합🈳",
  [PromptType.PHARMACY_PATIENT_EDU]: "약국용복약지도",
  [PromptType.POLY_PHARMACY_PATIENT_EDU]: "다제약물복약지도",
  [PromptType.INTERNAL_PRESCRIPTION_IMAGE]: "🤖처방전이미지분석",
  [PromptType.INTERNAL_PRESCRIPTION_OCR_TEXT]: "🤖처방전OCR텍스트분석",
  [PromptType.INTERNAL_REVIEW_EVALUATION]: "🤖분석결과평가",
  [PromptType.CONSULTATION]: "상담 기록",
  [PromptType.DOSE_EXTRACT_STEP2]: "룰 추출 STEP2 (품목 정체성)",
  [PromptType.DOSE_EXTRACT_STEP3]: "룰 추출 STEP3 (용량 룰)",
};

export const getPromptTypeKoreanName = (type?: PromptType | string): string => {
  if (!type) return "미분류🤖";

  // PromptType enum에 해당하는 값인지 확인
  const enumValues = Object.values(PromptType) as string[];
  if (enumValues.includes(type)) {
    return PROMPT_TYPE_KOREAN_NAMES[type as PromptType] || type;
  }

  // enum에 없는 값이면 그대로 반환
  return type;
};

// 프롬프트 인터페이스
export interface Prompt {
  id?: number;
  version?: string;
  promptTemplate?: string;
  promptType?: PromptType;
  description?: string;
  model?: string; // 기본으로 사용될 모델
  isActive?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  deletedAt?: Date | null;
  userId?: string | null;
}

// 사용 가능한 AI 모델 목록
export const AVAILABLE_AI_MODELS = [
  "GPT_4O",
  "GPT_O1",
  "GPT_O3",
  "GPT_5",
  "GPT_5_1",
  "GPT_5_REASON",
  // GPT 5.6 시리즈: 모델 티어(Sol/Terra/Luna) × reasoning effort(HIGH/XHIGH/MAX) 조합이 하나의 식별자
  // effort 실험용 전수 노출 — 실험 종료 후 축소 예정 (2026-07-10 요청 문서 참조)
  "GPT_5_6_SOL_HIGH",
  "GPT_5_6_SOL_XHIGH",
  "GPT_5_6_SOL_MAX",
  "GPT_5_6_TERRA_HIGH",
  "GPT_5_6_TERRA_XHIGH",
  "GPT_5_6_TERRA_MAX",
  "GPT_5_6_LUNA_HIGH",
  "GPT_5_6_LUNA_XHIGH",
  "GPT_5_6_LUNA_MAX",
  // GPT-5.6 Luna Fast: 같은 Luna를 OpenAI 우선 처리(Fast 모드, 2배 단가)로 호출. effort(HIGH/XHIGH/MAX) 조합이 하나의 식별자. 실험용 노출 — 실험 종료 후 축소 예정 (2026-10-06 요청 문서 참조)
  "GPT_5_6_LUNA_FAST_HIGH",
  "GPT_5_6_LUNA_FAST_XHIGH",
  "GPT_5_6_LUNA_FAST_MAX",
  // GPT-6 Luna (2026-09-22): 같은 조합 규약. 실험용 — spec 2026-09-23
  "GPT_6_LUNA_HIGH",
  "GPT_6_LUNA_XHIGH",
  "GPT_6_LUNA_MAX",
  // GPT-6 Luna Fast: 같은 GPT-6 Luna를 OpenAI 우선 처리(Fast 모드, 2배 단가)로 호출. effort(HIGH/XHIGH/MAX) 조합이 하나의 식별자. 실험용 노출 — 실험 종료 후 축소 예정 (2026-10-06 요청 문서 확장 절 참조)
  "GPT_6_LUNA_FAST_HIGH",
  "GPT_6_LUNA_FAST_XHIGH",
  "GPT_6_LUNA_FAST_MAX",
  "CLAUDE_4_SONNET",
  "OPUS",
  "GEMINI_1_5_PRO",
  "GEMINI_2_5_FLASH",
  "GEMINI_2_5_PRO",
  "GEMINI_3_PRO",
  "GEMINI_3_FLASH",
  "GEMINI_3_5_FLASH",
  "GEMINI_3_1_PRO",
  "GEMINI_3_7_FLASH",
  "GROK_4_0709",
  "GROK_3",
  // Grok 4.5: reasoning effort(LOW/MEDIUM/HIGH) 조합이 하나의 식별자
  // effort 실험용 전수 노출 — 실험 종료 후 축소 예정 (2026-07-10 요청 문서 참조)
  "GROK_4_5_LOW",
  "GROK_4_5_MEDIUM",
  "GROK_4_5_HIGH",
  // Grok 4.6: reasoning effort(LOW/MEDIUM/HIGH/XHIGH) 조합이 하나의 식별자 (4.5와 달리 XHIGH 허용)
  // effort 실험용 전수 노출 — 실험 종료 후 축소 예정 (2026-08-25 요청 문서 참조)
  "GROK_4_6_LOW",
  "GROK_4_6_MEDIUM",
  "GROK_4_6_HIGH",
  "GROK_4_6_XHIGH",
  // Grok 4.7: reasoning effort(HIGH/XHIGH) 조합이 하나의 식별자. 실험용 노출 — 실험 종료 후 축소 예정 (2026-10-06 요청 문서 참조)
  "GROK_4_7_HIGH",
  "GROK_4_7_XHIGH",
  // Muse Spark 1.3 (Meta): reasoning effort xhigh·max 조합이 하나의 식별자. 실험용 노출 — 실험 종료 후 축소 예정
  "MUSE_SPARK_1_3_XHIGH",
  "MUSE_SPARK_1_3_MAX",
  // Muse Spark 1.2 (Meta): 1.3이 dev 서버(AWS 발신 IP)에서 막혀 실험 대체 후보. xhigh만
  "MUSE_SPARK_1_2_XHIGH",
  "ON_PREMISE",
  "CLAUDE_OPUS_4_6",
  "CLAUDE_SONNET_4_6",
  "CLAUDE_OPUS_4_8",
  "CLAUDE_OPUS_5",
  "CLAUDE_SONNET_5",
  "SOLAR_OPEN2",
] as const;

export type AIModelType = (typeof AVAILABLE_AI_MODELS)[number];

export const DOC_NAME = {
  BEERS2023: "BEERS criteria",
  SPSTv3: "STOP & START",
  SPSTv2: "STOP & START",
  CCMM: "통합약물관리",
  LCMM: "요양시설가이드라인",
  KDGERI: "안전관리원_노인",
};

// docsOptions 문자열과 PromptType 간 매핑
export const DOCS_TO_GUIDELINE_MAPPING: { [key: string]: PromptType } = {
  BEERS2023: PromptType.GUIDELINE_BEERS2023,
  SPSTv3: PromptType.GUIDELINE_SPSTv3,
  SPSTv2: PromptType.GUIDELINE_SPSTv3,
  LCMM: PromptType.GUIDELINE_LCMM,
  CCMM: PromptType.GUIDELINE_CCMM,
  CMM_HTNDBS: PromptType.GUIDELINE_CMM_HTNDBS,
  KABS: PromptType.GUIDELINE_KABS,
  KDGERI: PromptType.GUIDELINE_KDGERI,
  POLY: PromptType.GUIDELINE_POLY,
};

// PromptType을 docsOptions 문자열로 변환하는 역방향 매핑
export const GUIDELINE_TO_DOCS_MAPPING: { [key in PromptType]?: string } = {
  [PromptType.GUIDELINE_BEERS2023]: "BEERS2023",
  [PromptType.GUIDELINE_SPSTv3]: "SPSTv3",
  [PromptType.GUIDELINE_LCMM]: "LCMM",
  [PromptType.GUIDELINE_CCMM]: "CCMM",
  [PromptType.GUIDELINE_CMM_HTNDBS]: "CMM_HTNDBS",
  [PromptType.GUIDELINE_KABS]: "KABS",
  [PromptType.GUIDELINE_KDGERI]: "KDGERI",
  [PromptType.GUIDELINE_POLY]: "POLY",
};

export interface Reference {
  drugs?: Drug[];
  hff?: Drug[];
  docs?: any; //[key:"BEARS","STOP-START-V3"]:DrugProductLabel[]
}

// 건기식 전용 모듈명 매핑 (백엔드 계획서 8.1절 참조)
export const HFF_MODULE_NAME_MAP: Record<string, string> = {
  INGREDI: "원재료",
  EE: "기능성",
  UD: "섭취방법",
  NB: "섭취 시 주의사항",
  STDR: "기준규격",
  STOR: "보관방법",
  EXPR: "유효기간",
};

/**
 * AI 검토 상태 열거형 (100단위로 정의)
 */
export enum AIReviewStatus {
  FAILED = -1,

  NONE = 0,
  PENDING = 100, // 대기열 상태
  READY = 200,

  // 처리 단계별 상태
  PROCESSING = 300,
  PROCESSING_USER_INFO = 310, // 회원정보 수집
  PROCESSING_PRESCRIPTION = 320, // 회원 처방전 정보 수집
  PROCESSING_DRUG_CRAWLING = 330, // 처방전 의약품 크롤링
  PROCESSING_DRUG_INFO = 340, // 처방전 의약품 수집
  PROCESSING_PROMPT_CREATION = 350, // LLM용 프롬프트 생성
  PROCESSING_LLM_ANALYSIS = 360, // LLM 분석

  COMPLETED = 400,
  // 확장을 위한 여유 공간 (500, 600, ... 등으로 확장 가능)
}

/**
 * AI 리뷰 요청 옵션 인터페이스
 */
export interface AIReviewRequest {
  modelType?: string;
  priority?: number;
  promptType?: PromptType | string;
  promptId?: number;
  aiSolution?: string;
  prescriptionIds?: number[];
  medicationOptions?: string[]; // UD, EE 등의 옵션 값 배열
  docsOptions?: string[]; // BEERS2023, SPSTv3 등의 옵션 값 배열

  // 관리자 옵션
  admin?: {
    targetProcessingStep?: AIReviewStatus; // 목표 처리 단계 (여기까지만 처리하고 중단)
    rerun?: boolean; // 다시 처음부터 실행 여부
  };

  // 초기 분석 정보
  initialAnalysis?: {
    prescriptionCount: number; // 처방전 개수
    totalMedicines: number; // 총 처방 의약품 수
    uniqueMedicines: number; // 중복 제거한 의약품 수
    referenceCount: number; // 허가사항 총 개수
    totalLength?: number; // @deprecated 더 이상 사용하지 않음 (호환성 유지)
    totalBytes?: number; // @deprecated 더 이상 사용하지 않음 (호환성 유지)
  };

  [key: string]: any; // 기타 설정을 위한 인덱스 시그니처
}

/**
 * AI 리뷰 성능 분석 정보 인터페이스
 */
export interface AIReviewAnalysis {
  processingTimeMs?: number;
  inputTokens?: number;
  outputTokens?: number;
  cost?: number;
  startTimestamp?: string | Date;
  endTimestamp?: string | Date;
  logs?: Array<{
    timestamp?: string | Date;
    message?: string;
    level?: string;
    [key: string]: any;
  }>;
  // 관리자 응답 전용 — LLM 실패 원문 한 줄과 그 기록 시각(ISO 8601).
  // spec: 2026-09-14-phai-관리자-LLM실패-원문-노출 · 설계 결정 4·13.
  llmFailureDetail?: string;
  llmFailureDetailAt?: string;
  [key: string]: any; // 기타 분석 데이터를 위한 인덱스 시그니처
}

// AI 검토 결과 인터페이스
export interface AIReview {
  // 주요 식별자 및 메타데이터
  id: number;
  userId?: string;
  user?: User;
  premiumRole?: string | null; // 프리미엄 권한 (예: "경기도약사회", "서울시약사회/구로구")
  patientId?: number;
  promptType?: string; // 추가: 프롬프트 타입 (medication_counseling 등)

  // 상태 및 우선순위
  status?: AIReviewStatus;
  priority?: number;

  // 처리 시간 관련
  startProcessingTime?: Date;
  endProcessingTime?: Date;
  reviewDate?: Date;

  // JSON 필드로 저장될 복잡한 객체들 (DB에는 JSON 문자열, 코드에서는 객체로 사용)
  request?: AIReviewRequest; // AI 리뷰 요청 옵션
  patientSnap?: Patient; // 수행 시점 환자, 처방전 정보
  referenceSnap?: Reference; // 수행 시점 참조 데이터
  analysis?: AIReviewAnalysis; // 성능 분석 정보

  // 핵심 요소
  actualPrompt?: string; // 프롬프트 실제 내용
  reviewResult?: string; // 분석 결과
  reviewResultEdited?: string; // 사용자가 수정한 분석 결과
  cost?: number; // 비용

  // 사용자 정의 필드
  pinnedAt?: Date; // 고정 시간
  memo?: string; // 사용자 메모

  // Hybrid Stream 관련 필드
  queuePosition?: number; // 대기열 위치
  totalQueue?: number; // 전체 대기열 수
  estimatedTimeSeconds?: number; // 예상 대기 시간 (초)
  processingMessage?: string; // 처리 중 메시지 (SSE로 전달받는 실시간 메시지)
  workerAvailable?: boolean; // 워커 사용 가능 여부

  // 기본 타임스탬프
  createdAt?: Date;
  updatedAt?: Date;
  deletedAt?: Date | null;

  // 추가 필드
  evaluationId?: number; // 평가 ID
  reasoningLog?: string; // AI 사고 과정 로그 (reasoning/thinking)

  // 피드백 관련
  feedbackId?: number | null; // 피드백 ID (있으면 피드백 존재, null이면 없음)
  feedback?: AIReviewFeedback; // Admin 전용: 피드백 상세 데이터

  // 포인트 관련
  pointsTransactionId?: number | null; // 포인트 차감 내역 ID (프리미엄은 null)

  // LLM 실패 사유 코드 (백엔드가 실패 시 기록·전송. 값이 없으면 null — 과거 건·조기 종료 건)
  llmFailureCode?: LlmFailureCode | null;
}

/**
 * LLM 호출 실패 사유 코드 5종 (백엔드 `ai_reviews.llm_failure_code` · SSE `status:-1` 이벤트).
 * spec: 2026-08-31-phai-웹-LLM실패-사용자안내 · 설계 결정 3.
 */
export type LlmFailureCode =
  | "RATE_LIMITED"
  | "TIMEOUT"
  | "PROVIDER_ERROR"
  | "TOKEN_LIMIT"
  | "UNKNOWN";

// 사용자 정보 인터페이스
export interface User {
  id: string; // UUID (Cognito ID)
  email?: string; // 이메일 주소
  name?: string; // 이름
  phone?: string; // 전화번호
  company?: string; // 회사명
  prompts?: PromptType[]; // 사용가능한 프롬프트 목록
  roles?: string[]; // 권한 레벨 (admin, user 등)
  password?: string; // 비밀번호 (생성 시에만 사용)
  lastLogin?: Date; // 마지막 로그인 시간
  createdAt?: Date; // 생성 시간
  updatedAt?: Date; // 수정 시간
  deletedAt?: Date; // 삭제 시간

  license?: string; // 라이센스 정보
  guidedVersion?: string; // 가이드 버전
  agreementVersion?: string; // 약관 동의 버전
  manualVersion?: string; // 마지막에 확인한 메뉴얼 버전

  userPoints?: UserPoints; // 사용자 포인트 정보
  ymydId?: number; // 약문약답 사용자 ID
  isPremium?: boolean; // 활성 유료 프리미엄 여부 (백엔드 계산값)
}

// 시스템에서 사용할 역할(권한) 타입
export enum UserRole {
  ADMIN = "admin",
  USER = "user",
  PREMIUM = "Premium",
  GYEONGGI_PHARMACIST = "경기도약사회",
  SEOUL_PHARMACIST = "서울시약사회",
  INCHEON_PHARMACIST = "인천시약사회",
}

/**
 * 프론트엔드에서 처방전 그룹 관리를 위한 인터페이스
 * 백엔드에서는 개별 Prescription으로 처리되지만 프론트엔드에서는 그룹으로 관리
 */
export interface PrescriptionGroup {
  // 그룹 식별자 (프론트엔드에서만 사용)
  patientId?: number;
  groupId?: string;
  // 처방일
  prescriptionDate?: Date | string;
  // 병원명
  hospital?: string;
  // AI 프롬프트용 병원 가명코드 (예: "A", "B")
  hospitalAlias?: string;
  // 병원 진료과·종별 라벨 (예: "정형외과의원", "의원/근골격계"). 병원 버튼화 복원 시 버튼 label로 사용.
  hospitalLabel?: string;
  // 그룹에 속한 처방약 목록
  prescriptions?: Prescription[];
  // 삭제된 처방약 ID 목록 (이미 저장된 처방약을 삭제할 때 사용)
  deletedPrescriptionIds?: number[];
}

/**
 * 프론트엔드에서 처방전 그룹 관리를 위한 인터페이스
 * 백엔드에서는 개별 Prescription으로 처리되지만 프론트엔드에서는 그룹으로 관리
 * 사용자 데이터로는 저장되지 않는다.
 */
export interface PrescriptionHospitalGroup {
  // 그룹 식별자 (프론트엔드에서만 사용)
  hospital?: string;
  prescriptionGroups: PrescriptionGroup[];
}

/**
 * 처방전 분석 상태 타입
 */
export enum AnalysisStatus {
  PENDING = "pending",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  FAILED = "failed",
}

/**
 * 시간 측정 정보 인터페이스
 */
export interface TimingInfo {
  // 필수 필드
  durationMs: number;
  status: "completed" | "failed" | "running" | "pending";

  // 선택적 필드
  startTime?: Date | string;
  endTime?: Date | string;
  description?: string;
}

/**
 * AI 리뷰 성능 분석 정보 인터페이스
 */
export interface AIReviewAnalysis {
  inputTokens?: number;
  outputTokens?: number;
  cost?: number;

  // LLM 모델 정보
  model?: string;

  // 단계별 타이밍 정보 (맵 형태로 변경)
  timings?: {
    // 단계별 타이밍 (번호 포함)
    "1_USER_INFO"?: TimingInfo;
    "2_PRESCRIPTION"?: TimingInfo;
    "3_DRUG_CRAWLING"?: TimingInfo;
    "4_DRUG_INFO"?: TimingInfo;
    "5_PROMPT_CREATION"?: TimingInfo;
    "6_LLM_ANALYSIS"?: TimingInfo;

    // 특수 타이밍 (번호 없음)
    TOTAL_PROCESSING?: TimingInfo; // start() 시작부터 완료까지의 시간
    LIFE?: TimingInfo; // 객체 생성부터 소멸까지의 시간
    LLM_FIRST_RESPONSE?: TimingInfo;

    // 기타 사용자 정의 타이밍
    [key: string]: TimingInfo;
  };

  logs?: Array<{
    timestamp?: string | Date;
    message?: string;
    level?: string;
    [key: string]: any;
  }>;
}

// 허가사항 코드와 설명 매핑
export const PERMISSION_DESCRIPTIONS = {
  EE: "효능효과",
  UD: "용법용량",
  WARN: "경고",
  CONT: "금기",
  CARE: "신중투여",
  SIDE: "이상반응",
  DDIN: "상호작용",
  ADMN: "적용상의 주의",
  PRCU: "임부 및 수유부",
  ACTN: "약리작용",
  OVER: "과량투여",
  CLIX: "임상검사치에의 영향",
  STOR: "보관 및 취급상의 주의",
  ELSE: "기타",
  EXPR: "전문가를 위한 정보",
  KDNY: "신장애 환자",
  LIVR: "간장애 환자",
  PRD: "성분 및 함량",
  UNDEF: "미분류",
  GNRL: "일반적 주의사항",
  LIKI: "신장애 및 간장애 환자 주의사항",
  SECTION: "섹션",
};

// 허가사항 옵션 프리셋
export const PERMISSION_PRESETS = {
  OPTION1: [
    "EE",
    "UD",
    "WARN",
    "CONT",
    "CARE",
    "SIDE",
    "DDIN",
    "ADMN",
    "PRCU",
    "ACTN",
    "CLIX",
    "STOR",
    "ELSE",
    "GNRL",
    "PRD",
    "LIKI",
    "LIVR",
    "KDNY",
    "SECTION",
  ],
  OPTION2: [
    "EE",
    "UD",
    "WARN",
    "CONT",
    "DDIN",
    "ADMN",
    "CLIX",
    "ELSE",
    "PRD",
    "STOR",
    "CLIX",
    "PRCU",
    "SECTION",
  ],
  ALL: Object.keys(PERMISSION_DESCRIPTIONS),
};

export const RequestPresets = {
  [PromptType.MEDICATION_COUNSELING]: {
    promptType: PromptType.MEDICATION_COUNSELING,
    docsOptions: ["SPSTv3", "POLY", "KDGERI"],
    medicationOptions: PERMISSION_PRESETS.OPTION2,
  },
  [PromptType.CMM_2]: {
    promptType: PromptType.CMM_2,
    docsOptions: ["CCMM", "KABS", "POLY"],
    medicationOptions: PERMISSION_PRESETS.OPTION2,
  },
  [PromptType.LCMM]: {
    promptType: PromptType.LCMM,
    docsOptions: ["LCMM", "KABS"],
    medicationOptions: PERMISSION_PRESETS.OPTION2,
  },
  default: {
    docsOptions: ["SPSTv3"],
    medicationOptions: PERMISSION_PRESETS.OPTION2,
  },
};

// 포인트 시스템 관련 인터페이스들

// 주문 정보 인터페이스
export interface OrderInfo {
  id?: number;
  userId?: string;
  productName?: string;
  points?: number;
  amount?: number;
  payRefund?: number;
  payAmount?: number;
  createdAt?: Date;
  orderedAt?: Date;
  paidAt?: Date;
  returnAt?: Date;
  returnedAt?: Date;
  deletedAt?: Date;
  payUid?: string;
  payCardName?: string;
  payMethod?: OrderPayMethod;
  paidAmount?: number;
  payStatus?: OrderPayStatus;
  payApplyNumber?: string;
  payReceiptUrl?: string;
  payMerchantId?: string;
  impUid?: string;
  canceledAmount?: number;
}

// 주문 결제 방법 열거형
export enum OrderPayMethod {
  CARD = "card",
  BANK_TRANSFER = "bank_transfer",
  VIRTUAL_ACCOUNT = "virtual_account",
  TRANSFER = "transfer",
  MOBILE_PHONE = "mobile_phone",
  GIFT_CERTIFICATE = "gift_certificate",
  UNKNOWN = "unknown",
}

// 주문 결제 상태 열거형
export enum OrderPayStatus {
  PENDING = "pending",
  PAID = "paid",
  CANCELLED = "cancelled",
  REFUNDED = "refunded",
}

// 포인트 거래 내역 인터페이스
export interface PointsTransaction {
  id?: number;
  userId: string;
  transactionType: PointsTransactionType;
  points: number;
  balanceAfter: number;
  referenceId?: string;
  referenceType?: PointsReferenceType;
  description?: string;
  createdAt?: Date;
}

// 포인트 거래 타입 열거형
export enum PointsTransactionType {
  PURCHASE = "purchase",
  ADMIN_GRANT = "admin_grant",
  AI_ANALYSIS = "ai_analysis",
  REFUND = "refund",
  BONUS = "bonus",
  EVENT = "event",
}

// 포인트 참조 타입 열거형
export enum PointsReferenceType {
  ORDER = "order",
  AI_REVIEW = "ai_review",
  REVIEW = "review",
}

// 사용자 포인트 현황 인터페이스
export interface UserPoints {
  userId: string;
  currentPoints: number;
  totalEarnedPoints: number;
  totalUsedPoints: number;
  hasFirstPurchase: boolean;
  updatedAt?: Date;
}

// 포인트 거래 생성 요청 인터페이스
export interface CreateTransactionDto {
  userId: string;
  points: number; // 변동량 (양수: 적립, 음수: 차감)
  transactionType: PointsTransactionType;
  referenceId?: string; // 참조 ID (주문ID, AI리뷰ID 등)
  referenceType?: PointsReferenceType;
  description?: string; // 거래 설명
}

// 포인트 구매 상품 정보 인터페이스
export interface PointProduct {
  id: number;
  name: string;
  price: number; // 원화 가격
  basePoints: number; // 기본 포인트
  bonusRate: number; // 보너스 비율 (%)
  totalPoints: number; // 총 지급 포인트
  isRecommended?: boolean; // 추천 상품 여부
}

// ==================== 유료 프리미엄 구독 ====================

export type SubscriptionPlanCode = "PHAI_PREMIUM_MONTHLY" | "PHAI_PREMIUM_YEARLY";

export type SubscriptionBillingCycle = "monthly" | "yearly";

export type SubscriptionStatus =
  | "NONE"
  | "PENDING"
  | "ACTIVE"
  | "PAST_DUE"
  | "CANCELLING"
  | "CANCELLED"
  | "EXPIRED";

export type SubscriptionPaymentStatus =
  | "SCHEDULED"
  | "PAID"
  | "FAILED"
  | "CANCELLED"
  | "REFUNDED";

export type SubscriptionAttemptType =
  | "INITIAL"
  | "RECURRING"
  | "RETRY"
  | "CHANGE_CARD";

export interface SubscriptionPlanMeta {
  code: SubscriptionPlanCode;
  name: string;
  amount: number;
  billingCycle: SubscriptionBillingCycle;
}

export interface SubscriptionInitDto {
  subscriptionId: number;
  customerUid: string;
  merchantUid: string;
  amount: number;
  productName: string;
  billingCycle: SubscriptionBillingCycle;
}

export interface MySubscriptionDto {
  status: SubscriptionStatus;
  planCode: SubscriptionPlanCode | null;
  planName: string | null;
  currentPeriodEnd: string | null;
  nextBillingAt: string | null;
  cardName: string | null;
  cardNumberMasked: string | null;
}

export interface SubscriptionPayment {
  id: number;
  subscriptionId: number;
  status: SubscriptionPaymentStatus;
  attemptType: SubscriptionAttemptType;
  merchantUid: string;
  impUid: string | null;
  amount: number;
  cancelledAmount: number;
  payMethod: string | null;
  cardName: string | null;
  applyNumber: string | null;
  receiptUrl: string | null;
  failureReason: string | null;
  reservedAt: string | null;
  paidAt: string | null;
  cancelledAt: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  createdAt: string;
}

// ==================== AI Review 평가 관련 타입 ====================

// Ground Truth 인터페이스
export interface GroundTruth {
  id?: number;
  patientId: number;
  promptType: PromptType;
  expectedContent: string;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy: string;
  deletedAt?: Date | null;
  // 선택적으로 포함되는 관계 데이터
  patient?: Patient;
}

// 테스트 실험 상태 열거형
export enum TestStatus {
  PENDING = "PENDING",
  RUNNING = "RUNNING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

// 테스트 실험 인터페이스
export interface TestExperiment {
  id?: number;
  name?: string;
  description?: string;
  configuration: TestConfiguration[]; // JSON으로 저장될 배열
  status: TestStatus;
  totalCount: number;
  completedCount: number;
  failedCount: number;
  startedAt?: Date;
  completedAt?: Date;
  createdAt?: Date;
  createdBy: string;
}

// 테스트 구성 인터페이스
export interface TestConfiguration {
  aiReviewRequest: AIReviewRequest;

  // 세 가지 모드 중 최소 하나는 필수
  patientIds?: number[]; // 모드 1: AI Review 생성 + 평가
  aiReviewIds?: number[]; // 모드 2: 기존 AI Review 평가
  aiReviewEvaluationIds?: number[]; // 모드 3: 기존 평가 재평가

  iterations: number; // 반복 횟수
}

// AI Review 평가 그룹 인터페이스
export interface AIReviewEvaluationGroup {
  id?: number;
  testGroupKey: string;
  evaluationGroupKey: string;
  aiReviewRequest: AIReviewRequest; // JSON으로 저장
  evaluationPromptId: number;
  evaluationModel?: string;

  // 집계 데이터
  totalExecutions: number;
  successCount: number;
  failedCount: number;
  averageScore?: number;
  minScore?: number;
  maxScore?: number;
  medianScore?: number;
  stdDeviationScore?: number;
  avgExecutionTime?: number;
  avgCost?: number;

  // 사용자 플래그
  scrapped: boolean;
  hidden: boolean;

  createdAt?: Date;
  updatedAt?: Date;
}

// 평가 상태 열거형
export enum EvaluationStatus {
  PENDING = "PENDING",
  RUNNING = "RUNNING",
  SUCCESS = "SUCCESS",
  FAILED = "FAILED",
  SKIPPED_AI_REVIEW_FAILED = "SKIPPED_AI_REVIEW_FAILED",
}

// AI Review 평가 인터페이스
export interface AIReviewEvaluation {
  id?: number;
  aiReviewId: number;
  patientId: number;
  testGroupKey: string;
  evaluationGroupId: number;
  experimentId: number;

  // 평가 설정
  evaluationPromptId: number;
  evaluationModel?: string;
  groundTruthSnapshot: string;

  // 평가 결과
  score: number;
  evaluationComments?: string;
  status: EvaluationStatus;
  errorMessage?: string;

  createdAt?: Date;
  evaluatedAt?: Date;
  deletedAt?: Date | null;

  aiReview?: AIReview;
}

// 프리미엄 면허번호 인터페이스
export interface PremiumLicense {
  id?: number;
  license: string; // Repository에서 복호화되어 반환 (평문)
  roles: string; // "서울시약사회/구로구" 형태 또는 "경기도약사회"
  createdAt?: Date;
  deletedAt?: Date | null;
}

/**
 * 의약품 식별 상태
 */
export enum DrugIdentificationStatus {
  PENDING = "pending",
  PROCESSING = "processing",
  COMPLETED = "completed",
  FAILED = "failed",
  SELECTED = "selected",
  WRONG = "wrong",
}

/**
 * 의약품 검색 조건 (AI 추출 또는 사용자 수정)
 */
export interface DrugSearchCondition {
  identifyText1?: string | string[];
  identifyText2?: string | string[];
  formulation?: string[];
  shape?: string[];
  color?: string[];
  line?: string[];
}

/**
 * 의약품 검색 결과 (pills.json 구조 기반)
 */
export interface DrugSearchResult {
  itemSeq: string;
  itemName: string;
  entpName: string;
  chart: string;
  formulation: string;
  shape: string;
  colorClass1: string;
  colorClass2?: string;
  printFront: string;
  printBack: string;
  lineFront: string;
  lineBack: string;
  itemImage: string;
}

/**
 * 의약품 검색 이력 항목
 */
export interface DrugSearchHistory {
  condition: DrugSearchCondition;
  result: DrugSearchResult[];
  historyAt: string;
}

/**
 * AI 분석 메트릭
 */
export interface DrugAiAnalyse {
  processingTimeMs: number;
  inputTokens: number;
  outputTokens: number;
  cost: number;
  model: string;
  analyzedAt: string;
  error: string | null;
}

/**
 * 의약품 식별 요청 정보
 */
export interface DrugIdentification {
  id?: number;
  requestId: string;
  serialId?: string;
  userId: string;
  patientId?: number | null;
  note?: string | null;
  user?: User;
  conditions?: DrugSearchCondition;
  results?: DrugSearchResult[];
  historys?: DrugSearchHistory[];
  status: DrugIdentificationStatus;
  aiAnalyse?: DrugAiAnalyse;
  selectedItemCode?: string;
  createdAt?: Date;
  updatedAt?: Date;
  deletedAt?: Date | null;
}

// ==================== AI Review 피드백 관련 타입 ====================

// 피드백 항목 타입 (보고서 섹션 기반)
export type FeedbackItem =
  | 'sideEffects'      // 부작용 (section_7, 8)
  | 'interactions'     // 상호작용 (section_9)
  | 'dosage'           // 용법용량 (section_6)
  | 'cascade'          // 처방연쇄 (section_7)
  | 'duplication'      // 중복처방 (section_3)
  | 'patientGuide'     // 복약지도문 (patient_guide)
  | 'physicianNote';   // 의료진전달내용 (section_14)

// 피드백 항목 라벨 상수
export const FEEDBACK_ITEMS: Record<FeedbackItem, string> = {
  sideEffects: '부작용',
  interactions: '상호작용',
  dosage: '용법용량',
  cascade: '처방연쇄',
  duplication: '중복처방',
  patientGuide: '복약지도문',
  physicianNote: '의료진전달내용',
};

// 피드백 항목 키 배열 (UI 렌더링용)
export const FEEDBACK_ITEM_KEYS: FeedbackItem[] = [
  'sideEffects',
  'interactions',
  'dosage',
  'cascade',
  'duplication',
  'patientGuide',
  'physicianNote',
];

// AI 검토안 피드백 인터페이스 (v3.0 - Backend API 스펙 반영)
export interface AIReviewFeedback {
  id: number;
  aiReviewId: number;
  userId: string;
  rating: number;                     // 0~5 (0 = 미평가)
  likes: FeedbackItem[] | null;       // 유용했던 항목
  dislikes: FeedbackItem[] | null;    // 아쉬운 항목
  comment: string | null;
  createdAt: string;                  // ISO 8601
  updatedAt: string;                  // ISO 8601
  deletedAt?: string | null;          // 삭제 시간

  // 관계 필드 (Admin 조회 시 포함)
  user?: User;
  aiReview?: AIReview;
}

// hasDetails는 클라이언트에서 계산
export const hasFeedbackDetails = (feedback: AIReviewFeedback | null): boolean => {
  if (!feedback) return false;
  return !!(feedback.likes?.length || feedback.dislikes?.length || feedback.comment);
};

// 피드백 제출 상태 (로컬 상태 관리용)
export interface FeedbackSubmissionState {
  submitted: boolean;
  rating?: 1 | 2 | 3 | 4 | 5;
  hasDetails: boolean;  // 상세 의견 제출 여부
  timestamp?: string;
}

// 별점 라벨 상수
export const RATING_LABELS: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: "매우 불만족",
  2: "불만족",
  3: "보통",
  4: "만족",
  5: "매우 만족",
};

// ==================== 상담 녹음 기록 관련 타입 ====================

/**
 * 상담 녹음 기록 상태
 */
export enum ConsultationRecordStatus {
  FAILED = -1,           // 실패
  PENDING = 0,           // 업로드 완료, 처리 대기 중
  STT_PROCESSING = 100,  // STT 처리 중
  LLM_PROCESSING = 200,  // LLM 처리 중
  COMPLETED = 300,       // 완료
}

/**
 * 상담 컨텍스트 (환자 정보 등)
 */
export interface ConsultationContext {
  patientName?: string;
  patientAge?: number;
  patientGender?: string;  // "M" | "F"
  conditions?: string;
  medications?: string;
  additionalInfo?: string;
}

// ==================== 텍스트 상담 기록(DRP 체크) 관련 타입 ====================
// spec: 2026-10-06-phai-상담기록-텍스트입력-DRP체크 (설계 결정 4·5·13)

/** DRP 표/목록 형식. "none"이면 보고서 미연결 또는 추출 실패. */
export type DrpFormat = "table" | "list" | "none";

/** DRP 원천(설계 결정 4): 수정본 / 원본 / 없음. */
export type DrpSource = "edited" | "original" | "none";

/**
 * 행의 복약지도 표시(설계 결정 21): "done" = O(복약지도함), "not_needed" = X(복약지도 안 함).
 * 빈 칸(아직 판단 안 함)은 값이 아니라 **키 없음**이다 — `null`은 보내지 않는다(서버 400).
 */
export type ChecklistGuidance = "done" | "not_needed";

/**
 * DRP 체크 행 — 요청·저장 모양(설계 결정 13·21). 목록 형식은 severity·type이 "".
 * `guidance`가 정본이고 `checked = (guidance === "done")`는 직렬화 때 파생해 함께 싣는다.
 */
export interface DrpChecklistRow {
  no: string;
  severity: string;
  type: string;
  summary: string;
  checked: boolean;
  guidance?: ChecklistGuidance;
}

/** 약사 확인·교육 체크 행(severity 금지). `checked`·`guidance` 규칙은 DRP 행과 같다. */
export interface EducationChecklistRow {
  no: string;
  type: string;
  summary: string;
  checked: boolean;
  guidance?: ChecklistGuidance;
}

/**
 * 텍스트 상담 기록 컨텍스트. `source: "text"`로 녹음 기록과 구분한다(설계 결정 3).
 * 서버 소유 키(reviewDate·updatedAt)는 응답에만 있고 요청에는 보내지 않는다(설계 결정 6).
 * `version`은 S2 이후 저장본이 2, 그 전 저장본이 1이다(설계 결정 21).
 */
export interface TextConsultationContext {
  source: "text";
  version: 1 | 2;
  consultedAt: string; // KST YYYY-MM-DD
  reviewId: number | null;
  reviewDate?: string | null; // 서버가 채움(KST YYYY-MM-DD)
  drpSource: DrpSource;
  drpFormat: DrpFormat;
  drpItems: DrpChecklistRow[];
  educationItems: EducationChecklistRow[];
  note: string;
  updatedAt?: string; // 서버가 채움(ISO, 수정 시)
}

/**
 * 상담 녹음 처리 분석 정보
 */
export interface ConsultationAnalysis {
  pipeline: string;
  sttModel: string;
  llmModel: string;
  startedAt?: string;
  completedAt?: string;
  processingTimeMs?: number;
  cost?: {
    stt: number;
    llm: number;
    total: number;
  };
  tokens?: {
    prompt: number;
    completion: number;
    total: number;
  };
  promptId?: number;
}

/**
 * 상담 녹음 기록 인터페이스
 */
export interface ConsultationRecord {
  id?: number;
  userId: string;
  patientId?: number | null;
  status: ConsultationRecordStatus;
  errorMessage?: string;
  recordContent?: string;
  context?: ConsultationContext;
  audioUrl?: string;
  audioDurationSeconds?: number;
  rawText?: string;
  analysis?: ConsultationAnalysis;
  createdAt?: Date;
  deletedAt?: Date | null;
}

/**
 * 상담 기록 목록 아이템 (목록 API 응답용)
 */
export interface ConsultationRecordListItem {
  id: number;
  patientId?: number | null;
  status: number;
  recordContentPreview?: string;
  createdAt: Date | string;
  // 목록 확장(추가형, spec 변경 사항): 텍스트 기록 식별·보고서 연결·상담일.
  source?: "text" | "audio";
  reviewId?: number | null;
  consultedAt?: string | null;
}

/**
 * Admin용 상담 녹음 기록 인터페이스
 */
export interface ConsultationRecordAdmin {
  id: number;
  userId: string;
  userName: string;
  userEmail?: string;
  status: ConsultationRecordStatus;
  errorMessage?: string;
  recordContent?: string;
  recordContentPreview?: string;
  context?: ConsultationContext;
  audioUrl?: string;
  audioDurationSeconds?: number;
  rawText?: string;
  analysis?: ConsultationAnalysis;
  createdAt: string;
}

/**
 * Admin 상담 기록 목록 응답 요약
 */
export interface ConsultationRecordSummary {
  totalRecords: number;
  completedRecords: number;
  failedRecords: number;
  pendingRecords: number;
  avgProcessingTimeMs: number;
  totalCost: number;
}

/**
 * Admin 상담 기록 목록 응답
 */
export interface AdminConsultationListResponse {
  records: ConsultationRecordAdmin[];
  total: number;
  limit: number;
  offset: number;
  summary: ConsultationRecordSummary;
}
